import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import { Share } from '@material-ui/icons';
import tinycolor from 'tinycolor2';
import LinesEllipsis from 'react-lines-ellipsis';
import responsiveHOC from 'react-lines-ellipsis/lib/responsiveHOC';
import _ from 'lodash';

import { setOpenSharingModal, setShowCountdownModule } from '../../actions/configurationActions';
import { DateTimeHelper } from '../../util/date-time-helper';
import { getPreviewLink, isTouchable, joinClassNames } from '../../util/util';

import ControlButton from '../Controls/ControlButton';

import './CountDown.scss';

const DEFAULT_COUNTDOWN_CONFIG = {
	customBackground: {
		url: '',
	},
	titlePosition: {
		x: 10,
		y: 15,
		fontsize: 'large',
	},
	eventStartPosition: {
		x: 10,
		y: 10,
		fontsize: 'small',
	},
	countdownPosition: {
		x: 10,
		y: 45,
		fontsize: 'small',
	},
};
const DEFAULT_COUNTDOWN_CONFIG_MOBILE = {
	customBackground: {
		url: '',
	},
	titlePosition: {
		x: 10,
		y: 15,
		fontsize: 'large',
	},
	eventStartPosition: {
		x: 10,
		y: 10,
		fontsize: 'small',
	},
	countdownPosition: {
		x: 10,
		y: 45,
		fontsize: 'small',
	},
};

interface CountDownProps {
	playerConfigs?: any;
	queryParams?: any;
	isMobile?: boolean;
	isInlinePlayer?: boolean;
}

const baseClassName = 'countdown';

const ResponsiveEllipsis = responsiveHOC()(LinesEllipsis);

const CountDown = forwardRef(({ playerConfigs, isInlinePlayer, queryParams, isMobile }: CountDownProps, ref) => {
	const { t, i18n } = useTranslation();
	const dispatch = useDispatch();
	const { mediaName, currentMediaId, mediasData, showCountdownModule, mediaType, accountId, embedShareSettings } = useSelector(
		(state) => (state as any).configurationReducer
	);

	const countdownInterval = useRef<any | null>(null);
	const [isInvisible, setIsInvisible] = useState(true);
	const [isTitleClamped, setIsTitleClamped] = useState(false);
	const [limitedTitleLines, setLimitedTitleLines] = useState(3);
	const [countdownAriaLabel, setCountdownAriaLabel] = useState('');

	const color = useMemo(() => (!playerConfigs ? '#000000' : playerConfigs?.playerStyling?.primaryColor ?? '#126cfc'), [playerConfigs]);
	const opacity = Number(playerConfigs?.playerStyling?.primaryOpacity ?? 100);
	const rgbaWithOpa = { ...tinycolor(color).toRgb(), a: opacity / 100 };
	const hoverStyle = isTouchable() ? {} : { backgroundColor: tinycolor(rgbaWithOpa).toRgbString() };

	const solidColorStyle = playerConfigs?.topControls?.showSolidBackgroundTopControl
		? {
				backgroundColor: tinycolor({
					...tinycolor(playerConfigs?.topControls?.solidBackgroundTopColor ?? '#00000').toRgb(),
					a: Number(playerConfigs?.topControls?.solidBackgroundTopControlOpacity ?? 100) / 100,
				}).toRgbString(),
			}
		: undefined;

	const showShareButton = useMemo(() => {
		if (queryParams?.sharing) {
			return queryParams?.sharing === "true";
		}
		return playerConfigs?.topControls?.enabled && playerConfigs?.topControls?.showSocialSharing;
	}, [playerConfigs?.topControls?.enabled, playerConfigs?.topControls?.showSocialSharing, queryParams?.sharing]);

	const currentMediaData = useMemo(() => {
		return (mediasData ?? []).find((m: any) => m.id === currentMediaId) ?? {};
	}, [currentMediaId, mediasData]);

	const countdownConfig = useMemo(() => {
		const defaultConfig = isMobile && !isInlinePlayer ? DEFAULT_COUNTDOWN_CONFIG_MOBILE : DEFAULT_COUNTDOWN_CONFIG;
		const finalConfig = { ...defaultConfig, order: ['eventStartPosition', 'titlePosition', 'countdownPosition'] };
		const liveCountdown = playerConfigs?.liveCountdown ?? {};
		if (!liveCountdown || !liveCountdown.coverStyle || liveCountdown.coverStyle === 'default') {
			return finalConfig;
		}

		const { customBackground, titlePosition, countdownPosition, eventStartPosition } = liveCountdown;
		finalConfig.customBackground.url =
			customBackground.enabled && customBackground.url ? customBackground.url : defaultConfig.customBackground.url;
		finalConfig.titlePosition = titlePosition.enabled
			? {
					x: titlePosition.x ?? defaultConfig.titlePosition.x,
					y: titlePosition.y ?? defaultConfig.titlePosition.y,
					fontsize: titlePosition.fontsize ?? defaultConfig.titlePosition.fontsize,
				}
			: defaultConfig.titlePosition;
		finalConfig.eventStartPosition = eventStartPosition.enabled
			? {
					x: eventStartPosition.x ?? defaultConfig.eventStartPosition.x,
					y: eventStartPosition.y ?? defaultConfig.eventStartPosition.y,
					fontsize: eventStartPosition.fontsize ?? defaultConfig.eventStartPosition.fontsize,
				}
			: defaultConfig.eventStartPosition;
		finalConfig.countdownPosition = countdownPosition.enabled
			? {
					x: countdownPosition.x ?? defaultConfig.countdownPosition.x,
					y: countdownPosition.y ?? defaultConfig.countdownPosition.y,
					fontsize: countdownPosition.fontsize ?? defaultConfig.countdownPosition.fontsize,
				}
			: defaultConfig.countdownPosition;
		return finalConfig;
	}, [isInlinePlayer, playerConfigs?.liveCountdown]);

	const onShareButtonClick = useCallback(() => {
		if (isMobile) {
			const paramConfigs = queryString.parse(window.location.search);
			const params = {
				mediaDetails: {
					id: mediaType === 'scenario' ? paramConfigs.scenarioId : paramConfigs.mediaId,
					title: mediaName,
				},
				configs: { id: paramConfigs.configId, ...playerConfigs },
				paramConfigs,
				accountId: accountId,
				type: mediaType,
				time: undefined,
				embedShareSettings: embedShareSettings,
			};
			navigator.share && navigator.share({ url: getPreviewLink(params) });
			return;
		}
		dispatch(setOpenSharingModal(true));
	}, [accountId, dispatch, embedShareSettings, mediaName, mediaType, playerConfigs]);

	const checkToShowCountdown = useCallback(() => {
		const evtStartTime = Date.parse(currentMediaData?.custom?.['event-start']);
		if (DateTimeHelper.checkTimeGreaterThan5Mins(evtStartTime) && DateTimeHelper.checkTimeDivideByNumber(evtStartTime)) {
			setCountdownAriaLabel(`${t('LABEL_LIVE_START_IN')} ${DateTimeHelper.formatHumanize(currentMediaData?.custom?.['event-start'], t)}`);
		}
		if (!DateTimeHelper.checkTimeGreaterThan5Mins(evtStartTime) && DateTimeHelper.checkTimeDivideByNumber(evtStartTime, 1)) {
			setCountdownAriaLabel(`${t('LABEL_LIVE_START_IN')} ${DateTimeHelper.formatHumanize(currentMediaData?.custom?.['event-start'], t)}`);
		}

		if (Date.now() < evtStartTime) {
			dispatch(setShowCountdownModule(true));
		} else {
			dispatch(setShowCountdownModule(false));
			countdownInterval.current && clearInterval(countdownInterval.current);
		}
	}, [currentMediaData?.custom, dispatch, t]);

	const _handleReflow = useCallback((rleState) => {
		const { clamped } = rleState;
		setIsTitleClamped(clamped);
		return rleState;
	}, []);

	useEffect(() => {
		checkToShowCountdown();
		countdownInterval.current = setInterval(function () {
			checkToShowCountdown();
		}, 1000);

		return () => countdownInterval.current && clearInterval(countdownInterval.current);
	}, [checkToShowCountdown, currentMediaData.custom, dispatch, showCountdownModule]);

	useEffect(() => {
		const timeoutRef = setTimeout(() => setIsInvisible(false), 1000);
		return () => clearTimeout(timeoutRef);
	}, []);

	useEffect(() => {
		isInvisible &&
			setCountdownAriaLabel(`${t('LABEL_LIVE_START_IN')} ${DateTimeHelper.formatHumanize(currentMediaData?.custom?.['event-start'], t)}`);
	}, [currentMediaData?.custom, isInvisible, t]);

	useImperativeHandle(ref, () => ({
		changeLimitedTitleLines: (value: number) => {
			if (value !== limitedTitleLines) {
				setLimitedTitleLines(value);
			}
		},
	}));

	const renderTitle = () => (
		<div
			key="titlePosition"
			className={joinClassNames(
				`${baseClassName}__title qplayer-light-font`,
				`${baseClassName}__font-${countdownConfig.titlePosition.fontsize}`
			)}
			aria-label={`${t('ARIA_LABEL_MEDIA_TITLE')}${currentMediaData.mediaName}`}
			style={{
				top: countdownConfig.titlePosition.y + '%',
				left: countdownConfig.titlePosition.x + '%',
				right: countdownConfig.titlePosition.x + '%',
			}}
			role="heading"
			aria-level={1}
		>
			<ResponsiveEllipsis
				aria-hidden={true}
				className="qplayer-light-font"
				text={currentMediaData.mediaName}
				maxLine={limitedTitleLines}
				ellipsis="..."
				trimRight
				basedOn="words"
				onReflow={_handleReflow}
			/>

			{isTitleClamped && (
				<div style={{ position: 'relative' }} aria-hidden={true}>
					<div
						aria-hidden={true}
						className={joinClassNames([
							'controlbar__control-tooltip',
							'controlbar__control-tooltip--light',
							`${baseClassName}__title--tooltip`,
							'countdown__font-small',
						])}
					>
						<div className="controlbar__control-tooltip-text qplayer-light-font countdown__font-small">{currentMediaData.mediaName}</div>
					</div>
				</div>
			)}
		</div>
	);

	const renderEventStart = () => (
		<div
			key={'eventStartPosition'}
			className={joinClassNames(
				`${baseClassName}__event-start-time`,
				`${baseClassName}__font-${countdownConfig.eventStartPosition.fontsize}`,
				'qplayer-semibold-font'
			)}
			style={{
				top: countdownConfig.eventStartPosition.y + '%',
				left: countdownConfig.eventStartPosition.x + '%',
			}}
			aria-label={`${t('ARIA_LABEL_START_DATE')}, ${DateTimeHelper.formatDate(currentMediaData?.custom?.['event-start'], t, i18n.language)}`}
		>
			<span className={'qplayer-semibold-font'} aria-hidden={true}>
				{DateTimeHelper.formatDate(currentMediaData?.custom?.['event-start'], t, i18n.language)}
			</span>
		</div>
	);

	const renderCountdown = () => {
		const evtStartTime = Date.parse(currentMediaData?.custom?.['event-start']);
		const countDownTime = Date.now() < evtStartTime ? DateTimeHelper.parseTimeToNumber(currentMediaData?.custom?.['event-start']) : '00:00';

		return (
			<div
				key={'countdownPosition'}
				className={joinClassNames(
					`${baseClassName}__event-countdown`,
					`${baseClassName}__font-${countdownConfig.countdownPosition.fontsize}`,
					'qplayer-semibold-font'
				)}
				style={{
					top: countdownConfig.countdownPosition.y + '%',
					left: countdownConfig.countdownPosition.x + '%',
				}}
			>
				<div className="qplayer-semibold-font" aria-label={countdownAriaLabel} role="alert" aria-live="assertive">
					<span className={'qplayer-semibold-font'} aria-hidden={true}>
						{t('LABEL_LIVE_START_IN')}
					</span>{' '}
					<div className={'qplayer-semibold-font'} aria-hidden={true}>
						{countDownTime}
					</div>
				</div>
			</div>
		);
	};

	// Make sure tab order is the same as what shows screen
	const renderOrder = _.sortBy(
		[
			{ element: 'eventStartPosition', value: countdownConfig.eventStartPosition.y },
			{ element: 'titlePosition', value: countdownConfig.titlePosition.y },
			{ element: 'countdownPosition', value: countdownConfig.countdownPosition.y },
		],
		'value'
	).map((m) => m.element);

	return showCountdownModule ? (
		<div
			className={joinClassNames(
				baseClassName,
				isMobile ? `${baseClassName}-mobile` : undefined,
				isInvisible ? `${baseClassName}--invisible` : undefined
			)}
			style={{
				backgroundImage: `url("${countdownConfig.customBackground.url}")`,
				...{ backgroundColor: tinycolor(rgbaWithOpa).darken(20).toHexString() },
			}}
		>
			{renderOrder.map((element) => {
				if (element === 'eventStartPosition') {
					return renderEventStart();
				}
				if (element === 'countdownPosition') {
					return renderCountdown();
				}
				if (element === 'titlePosition') {
					return renderTitle();
				}
				return null;
			})}

			{showShareButton && (
				<ControlButton
					icon={<Share htmlColor="#fff" titleAccess={t('SHARE_TOOLTIP')} aria-hidden={true} />}
					tabIndex={0}
					ariaLabel={t('SHARE')}
					tooltip={t('SHARE_TOOLTIP')}
					style={solidColorStyle}
					hoverStyle={hoverStyle}
					onClick={onShareButtonClick}
					className={`${baseClassName}__share-btn`}
					topControl
				/>
			)}
		</div>
	) : null;
});

export default CountDown;
